import React, { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import categoriesApi from '../data/categories';

interface Category {
    id: number | string;
    category_name: string;
}
interface Props {}

function Addnewparty(props: Props) {
    const navigate = useNavigate();
    const [categoriesList, setCategoriesList] = useState<Category[]>([]);
    const fetchCategories = useCallback(async () => {
        const res = await categoriesApi();
        if (Array.isArray(res)) {
            // If the API returns array of {id, category_name}, set as is.
            setCategoriesList(res as Category[]);
        }
    }, []);

    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    const [data, setData] = useState({
        date: '',
        partyName: '',
        display_name: '', // Added display_name field to state
        contactNumber: '',
        contactPerson: '',
        contactPersonNumber: '',
        total_amount: '',
        categories: [] as (number | string)[], // aligns with id type
    });

    const [showSuccessPopup, setShowSuccessPopup] = useState(false);
    const [successPartyId, setSuccessPartyId] = useState<string | number | null>(null);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);

    const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        setData({ ...data, [e.target.name]: e.target.value });
    };

    const handleCategory = (id: number | string) => {
        setData(prev => ({
            ...prev,
            categories: prev.categories.includes(id)
                ? prev.categories.filter(c => c !== id)
                : [...prev.categories, id]
        }));
    };

    // Adds party, then creates a "remaining" usermeta record for party_id with the total_amount
    const handleSubmit = async (e?: React.FormEvent) => {
        e?.preventDefault();
        setErrorMsg(null);
        // adapt keys for API
        const payload = {
            date: data.date,
            name: data.partyName,
            display_name: data.display_name, // Send display_name to API
            number: data.contactNumber,
            contact_person_name: data.contactPerson,
            contact_person_number: data.contactPersonNumber,
            categories: data.categories,
            total_amount: data.total_amount
        };

        try {
            // First, add the party.
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/auth/addparty`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify(payload)
                }
            );

            if (response.ok) {
                const result = await response.json();
                const addedPartyId = result.partyId || result.id || result.party_id; // fallback for various API return fields
                setSuccessPartyId(addedPartyId);

                // Now, add corresponding usermeta for 'remaining'
                // Only try if total_amount and partyId exist
                if (addedPartyId && data.total_amount) {
                    try {
                        await fetch(
                            `${process.env.REACT_APP_API_URL}/auth/updateusermeta`,
                            {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json'
                                },
                                body: JSON.stringify({
                                    meta_key: "remaining",
                                    meta_value: data.total_amount,
                                    party_id: addedPartyId
                                })
                            }
                        );
                        // Ignore result for now (display party add success even if usermeta fails)
                    } catch (usermetaError: any) {
                        // Optionally show a warning. For now, do not block popup.
                        console.warn('Usermeta creation failed', usermetaError);
                    }
                }

                setShowSuccessPopup(true);
            } else {
                const err = await response.json();
                setErrorMsg(err.message || "Failed to add party");
            }
        } catch (error) {
            setErrorMsg("An error occurred while adding the party.");
            console.error(error);
        }
    };

    // On closing popup, redirect!
    const handlePopupOk = () => {
        setShowSuccessPopup(false);
        navigate('/add-party');
    };

    return (
        <>
            <div className='p-6'>
                <div className='w-full flex justify-center'>
                    <h1 className='text-[28px] text-[#101828] font-[500]'>
                        Add new party
                    </h1>
                </div>
                <div className='bg-[#ffff] p-5 w-full rounded-[10px] mt-4'>
                    {errorMsg && (
                        <div className="mb-4 rounded-lg bg-red-50 border border-red-300 text-[#D32F2F] px-4 py-3 text-center">
                            <span>{errorMsg}</span>
                        </div>
                    )}
                    <form onSubmit={handleSubmit}>
                        <div>
                            <label>Date</label><br />
                            <input
                                type="date"
                                name="date"
                                onChange={handleInput}
                                className='w-[100%] border-[#D0D5DD] border-[1px] p-2 rounded-[8px] text-[#667085] text-[16px] outline-none mt-1'
                                value={data.date}
                                required
                            />
                        </div>

                        <div className='flex flex-wrap w-full justify-between mt-4 gap-4'>
                            <div className='w-[100%] md:w-[48%]'>
                                <label>Party Name</label><br />
                                <input
                                    type="text"
                                    name="partyName"
                                    placeholder='Your name'
                                    onChange={handleInput}
                                    className='w-[100%] border-[#D0D5DD] border-[1px] p-2 rounded-[8px] text-[#667085] text-[16px] outline-none mt-1'
                                    value={data.partyName}
                                    required
                                />
                            </div>

                            <div className='w-[100%] md:w-[48%]'>
                                <label>Contact number</label><br />
                                <input
                                    type="number"
                                    name="contactNumber"
                                    placeholder='+91 123 000-0000'
                                    onChange={handleInput}
                                    className='w-[100%] border-[#D0D5DD] border-[1px] p-2 rounded-[8px] text-[#667085] text-[16px] outline-none mt-1'
                                    value={data.contactNumber}
                                    required
                                />
                            </div>
                        </div>

                        {/* Display Name Field (new input added after Party Name and Contact Number fields) */}
                        <div className='flex flex-wrap w-full justify-between mt-4 gap-4'>
                            <div className='w-[100%] md:w-[48%]'>
                                <label>Display Name</label><br />
                                <input
                                    type="text"
                                    name="display_name"
                                    placeholder='Display name'
                                    onChange={handleInput}
                                    className='w-[100%] border-[#D0D5DD] border-[1px] p-2 rounded-[8px] text-[#667085] text-[16px] outline-none mt-1'
                                    value={data.display_name}
                                    required
                                />
                            </div>
                        </div>

                        <div className='flex flex-wrap w-full justify-between mt-4 gap-4'>
                            <div className='w-[100%] md:w-[48%]'>
                                <label>Contact person name</label><br />
                                <input
                                    type="text"
                                    name="contactPerson"
                                    placeholder='Your name'
                                    onChange={handleInput}
                                    className='w-[100%] border-[#D0D5DD] border-[1px] p-2 rounded-[8px] text-[#667085] text-[16px] outline-none mt-1'
                                    value={data.contactPerson}
                                    required
                                />
                            </div>

                            <div className='w-[100%] md:w-[48%]'>
                                <label>Contact person's  number</label><br />
                                <input
                                    type="number"
                                    name="contactPersonNumber"
                                    placeholder='+91 123 000-0000'
                                    onChange={handleInput}
                                    className='w-[100%] border-[#D0D5DD] border-[1px] p-2 rounded-[8px] text-[#667085] text-[16px] outline-none mt-1'
                                    value={data.contactPersonNumber}
                                    required
                                />
                            </div>
                        </div>

                        {/* Total Amount Field */}
                        <div className='flex flex-wrap w-full justify-between mt-4 gap-4'>
                            <div className='w-[100%] md:w-[48%]'>
                                <label>Total Amount</label><br />
                                <input
                                    type="number"
                                    name="total_amount"
                                    placeholder='Enter total amount'
                                    onChange={handleInput}
                                    className='w-[100%] border-[#D0D5DD] border-[1px] p-2 rounded-[8px] text-[#667085] text-[16px] outline-none mt-1'
                                    value={data.total_amount}
                                    required
                                />
                            </div>
                        </div>

                        <p className='mt-5'>Categories</p>
                        <div className="flex flex-wrap justify-between gap-4 p-2">
                            {categoriesList.map(cat => (
                                <label key={cat.id} className="flex items-center gap-2 cursor-pointer text-[#344054] text-[16px]">
                                    <input
                                        type="checkbox"
                                        checked={data.categories.includes(cat.id)}
                                        onChange={() => handleCategory(cat.id)}
                                    />
                                    {cat.category_name}
                                </label>
                            ))}
                        </div>

                        <button
                            type="submit"
                            className="mt-6 flex items-center justify-center gap-2 text-[16px] font-[500] text-[#fff] bg-gradient-to-r from-[#1CAC70] to-[#FF9500] py-2 rounded-[8px] px-8"
                        >
                            Submit
                        </button>
                    </form>
                </div>
            </div>

            {showSuccessPopup && (
                <div className="fixed inset-0 flex items-center justify-center bg-black/30 z-50">
                    <div className="bg-white rounded-2xl shadow-2xl relative max-w-md w-full px-7 pb-7 pt-5 text-center border-[0.5px] border-gray-200 animate-popin">
                        <div className="flex flex-col items-center mb-5 mt-2">
                            <div className="flex items-center justify-center rounded-full bg-green-100 w-16 h-16 mb-3">
                                {/* Sparkle, check, or celebration icon! */}
                                <svg width="36" height="36" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="12" fill="#4ade80"/><path d="M16.5 8.75018L10.875 15L7.5 11.4546" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            </div>
                            <h2 className="text-[22px] font-bold text-[#184D2B] mb-1">Party Added Successfully!</h2>
                            
                        </div>
                        <button
                            className="mt-3 px-10 py-2 bg-gradient-to-r from-[#1CAC70] to-[#FF9500] text-white font-semibold rounded-lg shadow transition hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1CAC70] text-lg"
                            onClick={handlePopupOk}
                        >
                            Go to Parties
                        </button>
                    </div>
                    {/* Must have close on backdrop as fallback */}
                    <div
                        className="fixed inset-0"
                        onClick={handlePopupOk}
                        style={{ zIndex: -1 }}
                    />
                </div>
            )}
            {/* subtle animation for popup */}
            <style>
                {`
                .animate-popin {
                    transform: scale(0.95);
                    opacity: 0;
                    animation: popin-fade-in 0.36s cubic-bezier(.82,1.9,.41,.94) forwards;
                }
                @keyframes popin-fade-in {
                    to {
                        transform: scale(1);
                        opacity: 1;
                    }
                }
                `}
            </style>
        </>
    )
}

export default Addnewparty
